import type { Player } from '@moussenaires/shared';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from './Modal.js';

interface PlayerFormModalProps {
  player?: Player;
  onClose: () => void;
  onSubmit: (input: { name: string; bio: string | null }) => void;
  onDelete?: () => void;
  isSaving: boolean;
}

export function PlayerFormModal({
  player,
  onClose,
  onSubmit,
  onDelete,
  isSaving,
}: PlayerFormModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(player?.name ?? '');
  const [bio, setBio] = useState(player?.bio ?? '');

  return (
    <Modal
      title={player ? t('roster.editPlayer') : t('roster.addPlayer')}
      onClose={onClose}
      width="sm"
      footer={
        <>
          {player && onDelete && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              style={{ marginRight: 'auto' }}
              aria-label={t('common.delete')}
              title={t('common.delete')}
              onClick={() => {
                if (window.confirm(t('roster.deletePlayerConfirm', { name: player.name })))
                  onDelete();
              }}
            >
              <Trash2 size={15} />
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim() || isSaving}
            onClick={() => onSubmit({ name: name.trim(), bio: bio.trim() || null })}
          >
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="player-name">{t('roster.name')}</label>
        <input
          id="player-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoFocus
        />
      </div>
      <div className="field">
        <label htmlFor="player-bio">
          {t('roster.bio')} <span className="field-optional">({t('common.optional')})</span>
        </label>
        <textarea
          id="player-bio"
          rows={3}
          value={bio}
          onChange={(event) => setBio(event.target.value)}
        />
      </div>
    </Modal>
  );
}
